import { useState, useCallback, useEffect, useMemo, ReactNode, useRef } from 'react';
import { debounce } from 'lodash';
import {
  SearchContext,
  SearchContextType,
  SectionExpansionState,
  SearchResult,
} from './SearchContext';
import { searchThroughAllData } from './searchUtils';
import { ConfigurationItemMap } from '../../../configuration-item-tables/shared/helpers';
import { RelatedListProperties } from '@server/documentation';
import { BASE_SECTION_EXPANSION } from './consts';
import { scrollToSection, scrollToTableRow, scrollToSectionMark } from './scrollingHelpers';
import { ActiveState } from '../../activity-state/helpers';
import { cleanupHighlights } from './sharedDomHelpers';

// Timing constants for scroll behavior
const SCROLL_EXPAND_DELAY = 100; // ms to wait for section expansion
const SCROLL_MARK_DELAY = 200; // ms to wait before scrolling to mark
const INITIAL_SCROLL_DELAY = 200; // ms to wait before initial scroll

export interface SearchProviderProps {
  children: ReactNode;
  itemId: string;
  dependsOnItems?: ConfigurationItemMap;
  usedByItems?: ConfigurationItemMap;
  configurationItems?: ConfigurationItemMap;
  relatedListsItems?: Record<string, RelatedListProperties[]>;
  scrollContainerRef?:
    | React.RefObject<HTMLElement>
    | React.MutableRefObject<HTMLElement | undefined>;
  saltoObjectTypes?: Pick<{ objectType: string; label: string }, 'objectType' | 'label'>[];
  dependsOnActiveState?: ActiveState;
  usedByActiveState?: ActiveState;
  configurationsActiveState?: ActiveState;
}

export const SearchProvider = ({
  children,
  itemId,
  dependsOnItems,
  usedByItems,
  configurationItems,
  relatedListsItems,
  scrollContainerRef,
  saltoObjectTypes,
  dependsOnActiveState,
  usedByActiveState,
  configurationsActiveState,
}: SearchProviderProps) => {
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchContextType['searchResults']>([]);
  const [searchResultsForTerm, setSearchResultsForTerm] = useState(''); // Track which term these results are for
  const [arrowNavigationMatchIdx, setCurrentMatch] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const hasScrolledToInitialMatch = useRef(false);
  const lastSearchTermRef = useRef('');
  const isInitialSearchForTerm = useRef(true); // Track if this is the first search for current term
  const lastScrolledMatchIdx = useRef(-1); // Track which match we last scrolled to

  const [sectionExpansion, setSectionExpansion] = useState<SectionExpansionState>({
    ...BASE_SECTION_EXPANSION,
  });

  const debouncedSearch = useMemo(
    () =>
      debounce((term: string) => {
        const isNewSearchTerm = lastSearchTermRef.current !== term;

        // Store the search term that generated these results
        lastSearchTermRef.current = term;

        if (!term.trim()) {
          setSearchResults([]);
          setSearchResultsForTerm('');
          setCurrentMatch(0);
          setSectionExpansion({ ...BASE_SECTION_EXPANSION });
          setIsSearching(false);
          lastSearchTermRef.current = '';
          isInitialSearchForTerm.current = true;
          lastScrolledMatchIdx.current = -1;
          return;
        }

        // Clean up all existing highlights before searching
        // This ensures the DOM is in a clean state for accurate text search
        if (scrollContainerRef?.current) {
          cleanupHighlights(scrollContainerRef.current);
        }

        const results = searchThroughAllData(
          term,
          {
            dependsOnItems,
            usedByItems,
            configurationItems,
            relatedListsItems,
          },
          saltoObjectTypes,
          scrollContainerRef,
          {
            dependsOnActiveState,
            usedByActiveState,
            configurationsActiveState,
          },
        );

        // Only update results if we're still searching for the same term
        if (lastSearchTermRef.current === term) {
          setSearchResults(results);
          setSearchResultsForTerm(term); // Track which term these results are for

          // Only reset match index and scroll flag for NEW search terms
          // This prevents jumping back to first match when data changes trigger a re-search
          if (isNewSearchTerm || isInitialSearchForTerm.current) {
            setCurrentMatch(0);
            hasScrolledToInitialMatch.current = false;
            isInitialSearchForTerm.current = false;
          }

          const newSectionExpansion = {
            ...BASE_SECTION_EXPANSION,
          };

          // Check which sections have matches and expand them
          results.forEach((result) => {
            switch (result.section) {
              case 'details':
                newSectionExpansion.details = true;
                break;
              case 'dependencies':
                newSectionExpansion.dependencies = true;
                break;
              case 'usedBy':
                newSectionExpansion.usedBy = true;
                break;
              case 'configurations':
                newSectionExpansion.configurations = true;
                break;
              case 'latestDeployed':
                newSectionExpansion.latestDeployed = true;
                break;
              case 'tags':
                newSectionExpansion.tags = true;
                break;
              case 'openAi':
                newSectionExpansion.openAi = true;
                break;
              case 'sourceCode':
                newSectionExpansion.sourceCode = true;
                break;
            }
          });

          setSectionExpansion(newSectionExpansion);
        }
        setIsSearching(false);
      }, 300),
    [
      dependsOnItems,
      usedByItems,
      configurationItems,
      relatedListsItems,
      saltoObjectTypes,
      scrollContainerRef,
      dependsOnActiveState,
      usedByActiveState,
      configurationsActiveState,
    ],
  );

  const onSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      if (term.trim()) {
        setIsSearching(true);
        // Mark as initial search for this term (user is typing)
        isInitialSearchForTerm.current = true;
      }
      debouncedSearch.cancel(); // Cancel any pending debounced search from previous term
      debouncedSearch(term);
    },
    [debouncedSearch],
  );

  const onNext = useCallback(() => {
    setCurrentMatch((prev) => {
      const newMatch = Math.min(prev + 1, searchResults.length - 1);
      return newMatch;
    });
  }, [searchResults.length]);

  const onPrevious = useCallback(() => {
    setCurrentMatch((prev) => {
      const newMatch = Math.max(prev - 1, 0);
      return newMatch;
    });
  }, []);

  const onClear = useCallback(() => {
    lastSearchTermRef.current = '';
    setSearchTerm('');
    setSearchResults([]);
    setSearchResultsForTerm('');
    setCurrentMatch(0);
    setSectionExpansion({ ...BASE_SECTION_EXPANSION });
    setIsSearching(false);
    isInitialSearchForTerm.current = true; // Reset for next search
    lastScrolledMatchIdx.current = -1; // Reset scroll tracking
  }, []);

  const handleSetSectionExpansion = useCallback(
    (section: keyof SectionExpansionState, expanded: boolean) => {
      setSectionExpansion((prev) => ({ ...prev, [section]: expanded }));
    },
    [],
  );

  const expandSectionForMatch = useCallback(
    (section: SearchResult['section']) => {
      const sectionMap: Record<SearchResult['section'], keyof SectionExpansionState> = {
        details: 'details',
        dependencies: 'dependencies',
        tags: 'tags',
        openAi: 'openAi',
        usedBy: 'usedBy',
        configurations: 'configurations',
        latestDeployed: 'latestDeployed',
        sourceCode: 'sourceCode',
      };

      const expansionKey = sectionMap[section];
      if (expansionKey) {
        handleSetSectionExpansion(expansionKey, true);
      }
    },
    [handleSetSectionExpansion],
  );

  const scrollToMatch = useCallback(
    (matchIndex: number, instant = false) => {
      const match = searchResults[matchIndex];
      if (!match) {
        return;
      }

      // Step 1: Ensure the section containing the match is expanded
      expandSectionForMatch(match.section);

      // Step 2: Wait for section to expand, then scroll to match
      const expandDelay = instant ? 0 : SCROLL_EXPAND_DELAY;
      setTimeout(() => {
        // For table matches, scroll to specific row
        if (match.tableType && match.rowIndex !== undefined) {
          scrollToTableRow({
            scrollContainerRef,
            tableType: match.tableType,
            rowIndex: match.rowIndex,
          });
        } else if (
          match.section === 'latestDeployed' ||
          match.section === 'openAi' ||
          match.section === 'sourceCode'
        ) {
          // For sections with highlighted text marks, scroll directly to the highlighted mark
          const markDelay = instant ? 0 : SCROLL_MARK_DELAY;
          setTimeout(() => {
            scrollToSectionMark({
              scrollContainerRef,
              section: match.section,
              occurrenceIndex: match.occurrenceIndex,
              instant,
            });
          }, markDelay);
        } else {
          // For other non-table matches (details, tags), scroll to section
          scrollToSection({ scrollContainerRef, section: match.section, instant });
        }
      }, expandDelay);
    },
    [scrollContainerRef, searchResults, expandSectionForMatch],
  );

  // Scroll to match when arrowNavigationMatchIdx changes
  useEffect(() => {
    // Only scroll if the match index actually changed
    if (
      searchResults.length > 0 &&
      arrowNavigationMatchIdx >= 0 &&
      lastScrolledMatchIdx.current !== arrowNavigationMatchIdx
    ) {
      lastScrolledMatchIdx.current = arrowNavigationMatchIdx;
      scrollToMatch(arrowNavigationMatchIdx);
    }
  }, [arrowNavigationMatchIdx, searchResults.length, scrollToMatch]);

  // Scroll to first match when search results appear (only once per search)
  useEffect(() => {
    if (searchResults.length > 0 && searchTerm && !hasScrolledToInitialMatch.current) {
      hasScrolledToInitialMatch.current = true;
      lastScrolledMatchIdx.current = 0; // Mark that we scrolled to match 0
      // Small delay to ensure sections have expanded, use instant scroll for first match
      setTimeout(() => {
        scrollToMatch(0, true); // true = instant scroll (no animation)
      }, INITIAL_SCROLL_DELAY);
    }
  }, [searchResults.length, searchTerm, scrollToMatch]);

  // Cleanup on item change
  useEffect(() => {
    onClear();
  }, [itemId, onClear]);

  // Cleanup pending debounced search on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Helper function to check if a table has search results
  const hasTableResults = useCallback(
    (section: SearchResult['section'], tableType: string) => {
      return searchResults.some(
        (result) => result.section === section && result.tableType === tableType,
      );
    },
    [searchResults],
  );

  const contextValue: SearchContextType = {
    isInputVisible,
    setIsInputVisible,

    searchTerm,
    arrowNavigationMatchIdx,
    totalMatches: searchResults.length,
    searchResults,
    searchResultsForTerm, // Add this so components can check if results match current term

    sectionExpansion,
    isSearching,
    onSearch,
    onNext,
    onPrevious,
    onClear,
    scrollToMatch,
    setSectionExpansion: handleSetSectionExpansion,
    hasTableResults,
  };

  return <SearchContext.Provider value={contextValue}>{children}</SearchContext.Provider>;
};
